<template>
  <Page>
    <div class="space-y-1">
      <section class="bg-background-card rounded-lg shadow-card p-1">
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-3">
            <Button
              type="button"
              aria-label="Back"
              label="Back"
              icon="fa-solid fa-arrow-left"
              rounded
              severity="contrast"
              @click="goBack"
            />
            <div>
              <h1 class="text-xl font-bold text-text">{{ isReadOnly ? 'View Bill of Material' : 'Edit Bill of Material' }}</h1>
              <p class="text-xs text-text-light">{{ isReadOnly ? 'Read-only view.' : 'Update an existing bill of material.' }}</p>
            </div>
          </div>

          <div class="flex items-center space-x-4">
            <div v-if="loading" class="text-xs text-text-light">Loading...</div>

            <button @click="showVersionModal = true"
              class="flex items-center bg-background px-4 py-2 rounded-full text-sm font-medium text-secondary hover:text-primary hover:bg-background-hover transition-colors shadow-xs border border-border">
              <i class="fa-solid fa-history mr-2"></i>
              <span>History</span>
            </button>

            <button v-if="!isReadOnly" @click="showCommentsModal = true"
              class="flex items-center bg-background px-4 py-2 rounded-full text-sm font-medium text-secondary hover:text-primary hover:bg-background-hover transition-colors shadow-xs border border-border">
              <i class="fa-solid fa-comment mr-2"></i>
              <span>Comments</span>
              <span v-if="commentCount > 0" class="ml-1 bg-accent text-white text-xs rounded-full px-2 py-0.5">{{ commentCount }}</span>
              <span v-if="commentCount === 0 && billOfMaterialId"
                class="ml-1 bg-gray-400 text-white text-xs rounded-full px-2 py-0.5" title="Debug: Count is 0">0</span>
            </button>
          </div>
        </div>
      </section>

      <div class="grid gap-1 lg:grid-cols-2">
        <BillOfMaterialSubBomsTreeTablePanel :nodes="subBomTreeNodes" />

        <BillOfMaterialEditPanel
          :fields="(bomFields as unknown as Array<FormFieldConfig<Record<string, unknown>> & { fullWidth?: boolean; type?: string }>)"
          :model="(form as unknown as Record<string, unknown>)"
          :selected-manufacturer="selectedManufacturer"
          :selected-status="selectedStatus"
          :loading="loading"
          :saving="saving"
          :error="error"
          :readonly="isReadOnly"
          @save="onSave"
          @cancel="goBack"
          @manufacturer-change="handleManufacturerChange"
          @status-change="handleStatusChange"
          @field-change="handleFieldChange"
        />

        <div class="lg:col-span-2">
          <div class="">
            <div class="overflow-hidden w-full border-b border-border">
              <SelectButton
                :model-value="activeTab"
                :options="tabOptions"
                option-label="label"
                option-value="id"
                class="w-full"
                :pt="selectButtonPt"
                @update:model-value="handleTabChange"
              >
                <template #option="{ option }">
                  <div class="flex items-center px-3 py-2">
                    <span class="text-xs font-medium">{{ option.label }}</span>
                  </div>
                </template>
              </SelectButton>
            </div>

            <div class="bg-background-card rounded-b-lg shadow-card p-2">
              <div v-show="activeTab === 'materials'">
                <p v-if="materialsError" class="text-sm text-error mb-2">{{ materialsError }}</p>
                <!-- The panels hide their action buttons when readonly; bind handlers directly.
                     A ternary like `isReadOnly ? undefined : openAddMaterials` compiles to an
                     inline statement that evaluates the function without calling it. -->
                <BillOfMaterialMaterialsTablePanel
                  :rows="bomMaterials"
                  :selection="selectedBomMaterials"
                  :loading="materialsLoading"
                  :readonly="isReadOnly"
                  @add="openAddMaterials"
                  @edit-selected="openEditSelected"
                  @remove-selected="confirmRemoveSelected"
                  @update:selection="updateSelectedMaterials"
                />
              </div>

              <div v-show="activeTab === 'subBoms'">
                <p v-if="subBomsError" class="text-sm text-error mb-2">{{ subBomsError }}</p>
                <BillOfMaterialSubBomsTablePanel
                  :rows="subBoms"
                  :selection="selectedSubBoms"
                  :loading="subBomsLoading"
                  :readonly="isReadOnly"
                  @add="openAddSubBoms"
                  @edit-selected="openEditSelectedSubBom"
                  @remove-selected="confirmRemoveSubBoms"
                  @update:selection="updateSelectedSubBoms"
                />
              </div>

              <div v-show="activeTab === 'assets'">
                <p v-if="assetsError" class="text-sm text-error mb-2">{{ assetsError }}</p>
                <BillOfMaterialAssignedAssetsTablePanel
                  :rows="assignedAssets"
                  :selection="selectedAssets"
                  :loading="assetsLoading"
                  :readonly="isReadOnly"
                  @add="openAddAssets"
                  @remove-selected="confirmRemoveAssets"
                  @update:selection="updateSelectedAssets"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddBillOfMaterialMaterialsModal
      v-model:visible="addMaterialsVisible"
      :existing-material-ids="existingMaterialIds"
      @confirm="handleAddMaterialsConfirm"
    />

    <EditBillOfMaterialMaterialPanel
      :visible="editMaterialVisible"
      :selected-material="editTargetMaterial"
      @close="closeEdit"
      @saved="handleEditSaved"
    />

    <AddBillOfMaterialSubBomsModal
      v-model:visible="addSubBomsVisible"
      :existing-ids="existingSubBomIds"
      :parent-id="billOfMaterialId ?? ''"
      @confirm="handleAddSubBomsConfirm"
    />

    <AddBillOfMaterialAssetsModal
      v-model:visible="addAssetsVisible"
      :existing-asset-ids="existingAssetIds"
      @confirm="handleAddAssetsConfirm"
    />

    <EditBillOfMaterialSubBomQuantityPanel
      :visible="editSubBomVisible"
      :selected-sub-bom="editTargetSubBom"
      @close="closeEditSubBom"
      @saved="handleEditSubBomSaved"
    />

    <CommentsModal
      v-model:visible="showCommentsModal"
      entityType="BillOfMaterials"
      :entityId="billOfMaterialId ?? ''"
      @comment-added="refreshCommentCount"
    />

    <VersionHistoryModal
      :visible="showVersionModal"
      :tabs="versionHistoryTabs"
      initialTabId="bom"
      @close="showVersionModal = false"
    />
  </Page>
</template>

<script setup lang="ts">
import SelectButton from 'primevue/selectbutton';
import Page from '@/shared/components/layouts/Page.vue';
import type { FormFieldConfig } from '@/shared/components/forms/types';
import BillOfMaterialEditPanel from '../../components/bill-of-materials/BillOfMaterialEditPanel.vue';
import BillOfMaterialSubBomsTreeTablePanel from '../../components/bill-of-materials/BillOfMaterialSubBomsTreeTablePanel.vue';
import BillOfMaterialMaterialsTablePanel from '../../components/bill-of-materials/BillOfMaterialMaterialsTablePanel.vue';
import BillOfMaterialSubBomsTablePanel from '../../components/bill-of-materials/BillOfMaterialSubBomsTablePanel.vue';
import BillOfMaterialAssignedAssetsTablePanel from '../../components/bill-of-materials/BillOfMaterialAssignedAssetsTablePanel.vue';
import AddBillOfMaterialMaterialsModal from '../../components/bill-of-materials/AddBillOfMaterialMaterialsModal.vue';
import AddBillOfMaterialAssetsModal from '../../components/bill-of-materials/AddBillOfMaterialAssetsModal.vue';
import EditBillOfMaterialMaterialPanel from '../../components/bill-of-materials/EditBillOfMaterialMaterialPanel.vue';
import AddBillOfMaterialSubBomsModal from '../../components/bill-of-materials/AddBillOfMaterialSubBomsModal.vue';
import EditBillOfMaterialSubBomQuantityPanel from '../../components/bill-of-materials/EditBillOfMaterialSubBomQuantityPanel.vue';
import { useBillOfMaterialEditPage } from '../../composables/useBillOfMaterialEditPage';
import { ref, computed, watch } from 'vue'
import { useUserRole } from '@/shared/composables/useUserRole';
import { isProjectViewerRole } from '@/shared/utils/projectAccess';
import { useComments } from '@/features/asset-hierarchy/composables/useComments'
import CommentsModal from '@/features/asset-hierarchy/components/Modals/CommentsModal.vue'
import VersionHistoryModal from '@/shared/components/modals/VersionHistoryModal.vue'
import type { AuditTrailTabConfig } from '@/shared/composables/useAuditTrailTabs'

definePage({
  meta: {
    title: 'Edit Bill of Material',
  },
});

const {
  activeTab,
  tabOptions,
  selectButtonPt,
  handleTabChange,
  goBack,
  billOfMaterialId,
  bomFields,
  form,
  loading,
  saving,
  error,
  handleManufacturerChange,
  handleStatusChange,
  handleFieldChange,
  onSave,
  selectedManufacturer,
  selectedStatus,
  bomMaterials,
  selectedBomMaterials,
  materialsLoading,
  materialsError,
  existingMaterialIds,
  addMaterialsVisible,
  editMaterialVisible,
  editTargetMaterial,
  openAddMaterials,
  handleAddMaterialsConfirm,
  openEditSelected,
  closeEdit,
  handleEditSaved,
  confirmRemoveSelected,
  updateSelectedMaterials,
  subBoms,
  subBomTreeNodes,
  subBomsLoading,
  subBomsError,
  selectedSubBoms,
  existingSubBomIds,
  addSubBomsVisible,
  editSubBomVisible,
  editTargetSubBom,
  openAddSubBoms,
  handleAddSubBomsConfirm,
  openEditSelectedSubBom,
  closeEditSubBom,
  handleEditSubBomSaved,
  confirmRemoveSubBoms,
  updateSelectedSubBoms,
  assignedAssets,
  selectedAssets,
  assetsLoading,
  assetsError,
  existingAssetIds,
  addAssetsVisible,
  openAddAssets,
  handleAddAssetsConfirm,
  confirmRemoveAssets,
  updateSelectedAssets,
} = useBillOfMaterialEditPage();

const { userRole } = useUserRole();
const isReadOnly = computed(() => isProjectViewerRole(userRole.value));

// Local UI state for Version and Comments
const showVersionModal = ref(false)
const showCommentsModal = ref(false)

const versionHistoryTabs = computed((): AuditTrailTabConfig[] => {
  const id = billOfMaterialId.value
  return [
    {
      id: 'bom',
      label: 'Bill of Materials',
      entityType: 'BillOfMaterials',
      entityId: id ?? null
    }
  ]
})

// Comments composable for Bill of Material
const commentsOptions = computed(() => ({ entityType: 'BillOfMaterials', entityId: billOfMaterialId.value ?? '' }))
const { commentCount, fetchCommentCount } = useComments(commentsOptions.value)

// Ensure comment count is loaded when we have an id
watch(() => billOfMaterialId.value, async (newId) => {
  if (newId) {
    await fetchCommentCount('BillOfMaterials', newId)
  }
}, { immediate: true })

async function refreshCommentCount() {
  if (billOfMaterialId.value) {
    await fetchCommentCount('BillOfMaterials', billOfMaterialId.value ?? '')
  }
}
</script>
